import { useEffect, useState } from "react";
import styled from "styled-components";
import { GetPopupData, GetPopupKeyword } from "../Api/callData.js";
import { ChangePopupToggle, ChangeShowPopup } from "../Api/sendData.js";
import { DataNotFound } from "../Api/dataNotFound.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(40px, 5vw, 56px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 12px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(32px, 4.6vw, 56px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #0f172a;
  margin: 0 0 12px;
`;

const HeroLead = styled.p`
  font-size: clamp(14.5px, 1.3vw, 16.5px);
  color: #64748b;
  margin: 0;
`;

/* ── Content ── */
const ContentSection = styled.section`
  padding: clamp(24px, 3vw, 40px) 0 clamp(80px, 10vw, 120px);
`;

const SettingsCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: linear-gradient(135deg, #fafbff 0%, #f5f3ff 100%);
  flex-wrap: wrap;

  .meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .meta .label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6366f1;
  }
  .meta .desc {
    font-size: 14px;
    color: #475569;
  }
`;

const CountControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  input {
    width: 80px;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #ffffff;
    font-size: 15px;
    color: #0f172a;
    font-family: inherit;
    text-align: center;
  }
  input:focus {
    outline: 0;
    border-color: #c7d2fe;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
  }
`;

const ChangeBtn = styled.button`
  padding: 10px 20px;
  border-radius: 999px;
  border: 0;
  background: #0f172a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: #1e293b;
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 460px;
  margin: 0 0 16px;
  padding: 6px 8px 6px 18px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #c7d2fe;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.08);
  }

  input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 15px;
    color: #0f172a;
    font-family: inherit;
  }
  input::placeholder {
    color: #94a3b8;
  }

  button {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 0;
    background: #0f172a;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease;
    font-family: inherit;
  }
  button:hover {
    background: #1e293b;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  .count {
    font-size: 13.5px;
    color: #94a3b8;
  }
  .count strong {
    color: #0f172a;
    font-weight: 600;
  }
`;

const WriteBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  box-shadow: 0 8px 20px -10px rgba(99, 102, 241, 0.55);
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const List = styled.div`
  border-top: 1px solid #e2e8f0;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr 110px 110px;
  align-items: center;
  gap: 16px;
  padding: 16px 12px;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  .index {
    font-size: 13px;
    color: #94a3b8;
    text-align: center;
  }

  .title-link {
    font-size: 15px;
    color: #0f172a;
    font-weight: 500;
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .title-link:hover {
    color: #6366f1;
  }

  .date {
    font-size: 13px;
    color: #94a3b8;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr auto;
    gap: 8px;
    padding: 16px 4px;

    .index,
    .date {
      display: none;
    }
  }
`;

const TogglePill = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid
    ${(p) => (p.$active ? "#a7f3d0" : "#fecaca")};
  background: ${(p) => (p.$active ? "#ecfdf5" : "#fef2f2")};
  color: ${(p) => (p.$active ? "#10b981" : "#dc2626")};
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  font-family: inherit;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.97);
  }
`;

/* ── Pagination ── */
const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 48px;
`;

const PageBtn = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "#ffffff"};
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$active ? "transparent" : "#cbd5e1")};
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── States ── */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 15px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ── Icons ── */
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const ChevronsLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 18l-6-6 6-6M19 18l-6-6 6-6" />
  </svg>
);
const ChevronsRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13 18l6-6-6-6M5 18l6-6-6-6" />
  </svg>
);

const PAGE_SIZE = 10;

export function Popup() {
  const [post, setPost] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [nextFirst, setNextFirst] = useState(0);
  const [nowPath, setNowPath] = useState("");

  const [showPopup, setShowPopup] = useState(0);
  const [countSubmit, setCountSubmit] = useState(false);
  const [popupToggle, setPopupToggle] = useState("");

  const [keyword, setKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    const lastSegment = window.location.pathname.split("/").pop();
    setNowPath(lastSegment);
  }, []);

  useEffect(() => {
    if (countSubmit) {
      if (window.confirm("Change the number of popups shown on the home page?")) {
        (async () => {
          const success = await ChangeShowPopup(showPopup);
          if (success) {
            window.location.reload();
          } else {
            alert("Failed to update popup settings.");
          }
        })();
      }
      setCountSubmit(false);
    }
  }, [countSubmit, showPopup]);

  useEffect(() => {
    if (popupToggle !== "") {
      if (window.confirm("Change popup active state?")) {
        (async () => {
          const success = await ChangePopupToggle(popupToggle);
          if (success) {
            window.location.reload();
          } else {
            alert("Failed to update popup settings.");
          }
        })();
      }
      setPopupToggle("");
    }
  }, [popupToggle]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      if (keyword === "") {
        const { postsData, lastVisibleDoc, querySnapShotAll, lastPost } =
          await GetPopupData(currentPage, lastVisible, nextFirst);
        setPost(postsData);
        setLastVisible(lastVisibleDoc);
        setTotalPosts(querySnapShotAll);
        setNextFirst(lastPost);
      } else {
        const { postsResData, lastResVisibleDoc, allUniqueDocs, lastResPost } =
          await GetPopupKeyword(keyword, currentPage, lastVisible, nextFirst);
        setPost(postsResData);
        setLastVisible(lastResVisibleDoc);
        setTotalPosts(allUniqueDocs);
        setNextFirst(lastResPost);
      }
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, currentPage]);

  const totalPages = Math.max(1, Math.ceil((totalPosts || 0) / PAGE_SIZE));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const submitSearch = () => {
    setKeyword(keywordInput.trim());
    setCurrentPage(1);
  };

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Admin</Eyebrow>
          <HeroTitle>Popups</HeroTitle>
          <HeroLead>
            Manage which popups appear on the home page and how many to show.
          </HeroLead>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          <SettingsCard>
            <div className="meta">
              <span className="label">Display setting</span>
              <span className="desc">
                Number of active popups shown to home page visitors.
              </span>
            </div>
            <CountControl>
              <input
                type="number"
                min="0"
                placeholder="0"
                onChange={(e) => setShowPopup(Number(e.target.value))}
              />
              <ChangeBtn type="button" onClick={() => setCountSubmit(true)}>
                Apply
              </ChangeBtn>
            </CountControl>
          </SettingsCard>

          <SearchBar>
            <input
              type="text"
              placeholder="Search popups"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
            />
            <button type="button" onClick={submitSearch} aria-label="Search">
              <SearchIcon />
            </button>
          </SearchBar>

          <TopBar>
            <div className="count">
              Total <strong>{totalPosts}</strong>
            </div>
            <WriteBtn href="/adminWrite">+ New popup</WriteBtn>
          </TopBar>

          {loading ? (
            <StateBox>
              <Spinner />
              <div>Loading…</div>
            </StateBox>
          ) : post && post.length > 0 ? (
            <>
              <List>
                {post.map((p) => (
                  <Row key={p.id}>
                    <span className="index">{p.overallIndex}</span>
                    <a className="title-link" href={`/popup/${p.id}`}>
                      {p.title}
                    </a>
                    <span className="date">
                      {p.reg_time.toDate().toISOString().split("T")[0]}
                    </span>
                    <TogglePill
                      type="button"
                      $active={p.popup_yn === "Y"}
                      onClick={() => setPopupToggle(p)}
                    >
                      {p.popup_yn === "Y" ? "Active" : "Inactive"}
                    </TogglePill>
                  </Row>
                ))}
              </List>

              <Pagination aria-label="Pagination">
                <PageBtn
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="First page"
                >
                  <ChevronsLeftIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon />
                </PageBtn>
                {[...Array(totalPages)].map((_, i) => (
                  <PageBtn
                    key={i}
                    $active={currentPage === i + 1}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </PageBtn>
                ))}
                <PageBtn
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Next page"
                >
                  <ChevronRightIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Last page"
                >
                  <ChevronsRightIcon />
                </PageBtn>
              </Pagination>
            </>
          ) : (
            <DataNotFound table={nowPath} />
          )}
        </Container>
      </ContentSection>
    </Page>
  );
}
